// components/GraphPlotter/GraphViewer.js
import React, { useRef, useContext } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import { graphThemes } from "../../themes/graphThemes";
import generateSegmentTraces from "./segments/generateSegmentTraces";
import { generateRationalData } from "./generators/generateRationalData";
import * as math from "mathjs";
import Plot from "react-plotly.js";

const DEFAULT_FUNCTION_COLOR = "#ff9800";

function formatPointLabel(label) {
  if (!label) return label;

  return String(label).replace(/([A-Za-z])_([A-Za-z0-9]+)/g, function (_, base, subscript) {
    return base + "<sub>" + subscript + "</sub>";
  });
}

function normalizeRange(range, fallback) {
  if (!Array.isArray(range) || range.length !== 2) return fallback.slice();

  const min = Number(range[0]);
  const max = Number(range[1]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return fallback.slice();

  // Plotly mutates layout ranges in place. If a previously saved snapshot
  // contains a collapsed range, use a sane default instead of rendering a blank plot.
  if (Math.abs(max - min) < 0.1) return fallback.slice();

  return [min, max];
}

export default function GraphViewer({
  points = {},
  equations = [],
  segments = [],
  curves = [],
  verticalBands = [],
  circles = [],
  arcs = [],
  showQuadrants = false,
  showCoordinates = false,
  showProjections = false,
  xRange,
  yRange,
  width,
  height,
  preserveSquare = true,
  squareFrame = true,
  mathLabels = false,
  showGrid = true,
  yTickStep = 1,
}) {
  const plotRef = useRef();
  const { theme } = useContext(ThemeContext) || {};
  const style = graphThemes[theme === "dark" ? "cyberpunk" : "classic"];
  const mathFont = mathLabels ? { family: "KaTeX_Main, Times New Roman, serif" } : {};

  const currentRange = {
    x: normalizeRange(xRange, [-6, 6]),
    y: normalizeRange(yRange, [-6, 6]),
  };

  const parsedSegments = generateSegmentTraces(segments, points);

  const curveTraces = curves.map((curve, index) => ({
    x: curve.x,
    y: curve.y,
    type: "scatter",
    mode: "lines",
    line: {
      color: curve.color || DEFAULT_FUNCTION_COLOR,
      width: curve.width || 6,
      dash: curve.dash || "solid",
    },
    name: `Krzywa ${index + 1}`,
    hoverinfo: "skip",
    showlegend: false,
  }));

  const pointTraces = Object.entries(points)
    .filter(([_, p]) => p.showDot !== false && p.showDot !== "line")
    .map(([label, p]) => {
      const showLabel = p.showLabel !== false;
      const markerOn = p.showMarker !== false;
      const isOpen = p.markerSymbol === "circle-open";
      const markerSymbol =
        p.markerSymbol === "circle-filled" ? "circle" : p.markerSymbol || "circle";
      const pointColor = p.color || DEFAULT_FUNCTION_COLOR;
      const textColor = p.textColor || pointColor;

      return {
        x: [p.x],
        y: [p.y],
        mode: markerOn ? (showLabel ? "markers+text" : "markers") : "text",
        type: "scatter",
        marker: markerOn
          ? {
              color: isOpen ? style.backgroundColor : pointColor,
              size: 10,
              symbol: isOpen ? "circle" : markerSymbol,
              line: isOpen ? { width: 2, color: pointColor } : undefined,
            }
          : undefined,
        text: showLabel
          ? [
              showCoordinates
                ? `${formatPointLabel(p.label || label)} = (${p.x}, ${p.y})`
                : formatPointLabel(p.label || label),
            ]
          : [""],
        textfont: { color: textColor, size: 14, ...mathFont },
        textposition: p.textPosition || (p.x === 0 ? "top right" : "top center"),
        name: label,
        hoverinfo: "skip",
        showlegend: false,
      };
    });

  const labelOnlyTraces = Object.entries(points)
    .filter(([_, p]) => p.showDot === false && p.showLabel !== false)
    .map(([label, p]) => ({
      x: [p.x],
      y: [p.y],
      mode: "text",
      type: "scatter",
      text: [formatPointLabel(p.label || label)],
      textfont: {
        color: p.textColor || p.color || DEFAULT_FUNCTION_COLOR,
        size: p.labelSize || (p.label === "\u03B1" ? 18 : 14),
        family: mathLabels ? "KaTeX_Main, Times New Roman, serif" :
          p.label === "\u03B1"
            ? "Cambria Math, Times New Roman, serif"
            : "Arial",
      },
      textposition: p.textPosition || (p.x === 0 ? "top right" : "top center"),
      name: label,
      hoverinfo: "skip",
      showlegend: false,
    }));

  const defaultColors = [DEFAULT_FUNCTION_COLOR, "#8000FF", "#2ECC40", "#FF851B", "#B10DC9"];

  const functionTraces = equations.flatMap((item, i) => {
    const expr = typeof item === "string" ? item : item.expr;
    const color = typeof item === "object" && item.color ? item.color : defaultColors[i % defaultColors.length];
    const dash = typeof item === "object" && item.dash ? item.dash : "solid";
    const width = typeof item === "object" && item.width ? item.width : 2;
    const rhs = String(expr).replace("y=", "");

    if (rhs.includes("/")) {
      const { segments, asymptotes } = generateRationalData(
        rhs,
        currentRange.x[0],
        currentRange.x[1],
        currentRange.y[0],
        currentRange.y[1],
        0.05
      );

      const segmentTraces = segments.map((segment, segmentIndex) => ({
        x: segment.x,
        y: segment.y,
        type: "scatter",
        mode: "lines",
        line: { width, color, dash },
        name: segmentIndex === 0 ? `f${i + 1}` : undefined,
        showlegend: false,
        hoverinfo: "skip",
      }));

      const asymptoteTraces = asymptotes
        .filter((x) => x >= currentRange.x[0] && x <= currentRange.x[1])
        .map((x, asymptoteIndex) => ({
          x: [x, x],
          y: [currentRange.y[0], currentRange.y[1]],
          type: "scatter",
          mode: "lines",
          line: {
            width: 2,
            color,
            dash: "dash",
          },
          name: asymptoteIndex === 0 && segmentTraces.length === 0 ? `f${i + 1}` : undefined,
          showlegend: false,
          hoverinfo: "skip",
          opacity: 0.65,
        }));

      return [...segmentTraces, ...asymptoteTraces];
    }

    const fn = math.compile(rhs);
    const xs = math.range(currentRange.x[0], currentRange.x[1], 0.1).toArray();
    const ys = xs.map((x) => {
      try {
        return fn.evaluate({ x });
      } catch {
        return null;
      }
    });

    return [{
      x: xs,
      y: ys,
      type: "scatter",
      mode: "lines",
      line: { width, color, dash },
      name: `f${i + 1}`,
      showlegend: false,
    }];
  });

  const circleTraces = circles.map(({ center, radius, color = "#8000FF", width: lineWidth = 2 }, i) => {
    const step = 0.05;
    const t = math.range(0, 2 * Math.PI + step, step).toArray();
    const x = t.map((th) => center.x + radius * Math.cos(th));
    const y = t.map((th) => center.y + radius * Math.sin(th));
    return {
      x,
      y,
      mode: "lines",
      type: "scatter",
      line: { color, width: lineWidth },
      name: `Okrag ${i + 1}`,
    };
  });

  const arcTraces = arcs.map(
    ({ center, radius, startDeg, endDeg, color = "#8000FF", width: lineWidth = 3 }, i) => {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const a0 = toRad(startDeg);
      const a1 = toRad(endDeg);
      const step = (a1 - a0) / 60;
      const t = math.range(a0, a1, step || 0.01).toArray();

      const x = t.map((th) => center.x + radius * Math.cos(th));
      const y = t.map((th) => center.y + radius * Math.sin(th));

      return {
        x,
        y,
        mode: "lines",
        type: "scatter",
        line: { color, width: lineWidth },
        name: `Luk ${i + 1}`,
        hoverinfo: "skip",
        showlegend: false,
      };
    }
  );

  const projectionTraces = showProjections
    ? Object.entries(points).flatMap(([label, p]) => {
        const projectionColor = p.projectionColor || p.textColor || p.color || DEFAULT_FUNCTION_COLOR;
        const yLabelX = Math.min(0, p.x) - (p.projectionLabelOffsetX || 0.45);
        const xLabelY = Math.min(0, p.y) - (p.projectionLabelOffsetY || 0.45);

        return [
          {
            x: [0, p.x],
            y: [p.y, p.y],
            mode: "lines",
            type: "scatter",
            line: { color: projectionColor, dash: "dot" },
            showlegend: false,
            hoverinfo: "skip",
          },
          {
            x: [yLabelX],
            y: [p.y],
            mode: "text",
            type: "scatter",
            text: [formatPointLabel(`y_${label}`)],
            textposition: p.projectionTextPositionY || "middle right",
            textfont: { size: 12, color: projectionColor },
            showlegend: false,
            hoverinfo: "skip",
          },
          {
            x: [p.x, p.x],
            y: [0, p.y],
            mode: "lines",
            type: "scatter",
            line: { color: projectionColor, dash: "dot" },
            showlegend: false,
            hoverinfo: "skip",
          },
          {
            x: [p.x],
            y: [xLabelY],
            mode: "text",
            type: "scatter",
            text: [formatPointLabel(`x_${label}`)],
            textposition: p.projectionTextPositionX || "top center",
            textfont: { size: 12, color: projectionColor },
            showlegend: false,
            hoverinfo: "skip",
          },
        ];
      })
    : [];

  const horizontalMarkerTraces = Object.entries(points)
    .filter(([_, p]) => p.showDot === "line")
    .map(([label, p]) => ({
      x: [p.x - 0.2, p.x + 0.2],
      y: [p.y, p.y],
      mode: "lines+text",
      type: "scatter",
      line: { color: p.color || DEFAULT_FUNCTION_COLOR, width: 3 },
      text: [formatPointLabel(p.label || label)],
      textposition: p.labelPosition || p.textPosition || "top center",
      textfont: { color: p.textColor || p.color || DEFAULT_FUNCTION_COLOR, size: 14 },
      showlegend: false,
      hoverinfo: "skip",
    }));

  const data = [
    ...functionTraces,
    ...parsedSegments,
    ...curveTraces,
    ...circleTraces,
    ...arcTraces,
    ...pointTraces,
    ...projectionTraces,
    ...(showQuadrants
      ? [
          {
            type: "scatter",
            x: [3.5, -3.5, -3.5, 3.5],
            y: [3.5, 3.5, -3.5, -3.5],
            mode: "text",
            text: ["I", "II", "III", "IV"],
            textfont: { size: 18, color: "gray" },
            showlegend: false,
            hoverinfo: "skip",
          },
        ]
      : []),
    ...labelOnlyTraces,
    ...horizontalMarkerTraces,
  ];

  const axisArrowAnnotations = [
    {
      x: 1,
      y: 0,
      ax: -24,
      ay: 0,
      xref: "x domain",
      yref: "y",
      axref: "pixel",
      ayref: "pixel",
      text: "",
      showarrow: true,
      arrowhead: 3,
      arrowsize: 1.2,
      arrowwidth: 2,
      arrowcolor: style.axisColor,
    },
    {
      x: 0,
      y: 1,
      ax: 0,
      ay: 24,
      xref: "x",
      yref: "y domain",
      axref: "pixel",
      ayref: "pixel",
      text: "",
      showarrow: true,
      arrowhead: 3,
      arrowsize: 1.2,
      arrowwidth: 2,
      arrowcolor: style.axisColor,
    },
    {
      x: 1,
      y: 0,
      xref: "x domain",
      yref: "y",
      text: "x",
      showarrow: false,
      font: { color: style.axisColor, size: 14, ...mathFont },
      xshift: -8,
      yshift: -16,
      xanchor: "right",
      yanchor: "top",
    },
    {
      x: 0,
      y: 1,
      xref: "x",
      yref: "y domain",
      text: "y",
      showarrow: false,
      font: { color: style.axisColor, size: 14, ...mathFont },
      xanchor: "left",
      xshift: 14,
      yshift: -12,
      yanchor: "top",
    },
  ];

  // The short annotation shaft completes each axis up to its arrowhead.
  // Stop the thicker zero-axis line earlier so it cannot cross the arrowhead.
  const axisLineShapes = [
    {type:"line",xref:"x domain",yref:"y",x0:0,x1:0.96,y0:0,y1:0,line:{color:style.axisColor,width:3},layer:"below"},
    {type:"line",xref:"x",yref:"y domain",x0:0,x1:0,y0:0,y1:0.96,line:{color:style.axisColor,width:3},layer:"below"},
  ];

  const layout = {
    paper_bgcolor: style.backgroundColor,
    plot_bgcolor: style.backgroundColor,
    font: { color: style.axisColor },
    xaxis: {
      title: "",
      range: currentRange.x.slice(),
      showline: true,
      linecolor: style.axisColor,
      linewidth: 2,
      zeroline: false,
      zerolinecolor: style.axisColor,
      zerolinewidth: 3,
      gridcolor: style.gridColor,
      showgrid: showGrid,
      tick0: 0,
      dtick: 1,
      ...(mathLabels ? { tickfont: mathFont } : {}),
      autorange: false,
      automargin: true,
      ...(preserveSquare ? { scaleanchor: "y", scaleratio: 1 } : {}),
    },
    yaxis: {
      title: "",
      range: currentRange.y.slice(),
      showline: true,
      linecolor: style.axisColor,
      linewidth: 2,
      zeroline: false,
      zerolinecolor: style.axisColor,
      zerolinewidth: 3,
      gridcolor: style.gridColor,
      showgrid: showGrid,
      tick0: 0,
      dtick: yTickStep,
      ...(mathLabels ? { tickfont: mathFont } : {}),
      autorange: false,
      automargin: true,
      ...(preserveSquare ? { scaleanchor: "x", scaleratio: 1 } : {}),
    },
    margin: { t: 20, b: 40, l: 40, r: 20 },
    hovermode: false,
    showlegend: false,
    annotations: axisArrowAnnotations,
    shapes: [...axisLineShapes, ...verticalBands.map((band) => {
      const bandYRef = band.yref || "paper";
      return {
        type: "rect",
        xref: "x",
        yref: bandYRef,
        x0: band.left,
        x1: band.right,
        y0: band.y0 ?? (bandYRef === "paper" ? 0 : currentRange.y[0]),
        y1: band.y1 ?? (bandYRef === "paper" ? 1 : currentRange.y[1]),
        fillcolor: band.color || "#8b5cf6",
        opacity: band.opacity ?? 0.12,
        line: { width: 0 },
        layer: "below",
      };
    })],
  };

  const maxWidth = Number.isFinite(width) ? width : 420;
  const maxHeight = Number.isFinite(height) ? height : maxWidth;
  const supportsAR =
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("aspect-ratio: 1 / 1");

  let wrapperStyle;
  let innerStyle;

  if (squareFrame) {
    wrapperStyle = supportsAR
      ? {
          width: `min(100%, ${maxWidth}px)`,
          aspectRatio: "1 / 1",
        }
      : {
          width: `min(100%, ${maxWidth}px)`,
          position: "relative",
          paddingTop: "100%",
        };

    innerStyle = supportsAR
      ? { width: "100%", height: "100%" }
      : { position: "absolute", inset: 0 };
  } else {
    wrapperStyle = {
      width: `min(100%, ${maxWidth}px)`,
      height: `${maxHeight}px`,
    };
    innerStyle = { width: "100%", height: "100%" };
  }

  return (
    <div style={wrapperStyle}>
      <div style={innerStyle}>
        <Plot
          ref={plotRef}
          data={data}
          layout={layout}
          config={{ scrollZoom: false, displayModeBar: false, responsive: true }}
          style={{ width: "100%", height: "100%" }}
          useResizeHandler
        />
      </div>
    </div>
  );
}



